/* BillingTab — two annual-only plan cards.
   Removed the monthly/annual toggle: MyLeaf is one-time + $12/year keeping fee.
   Wider, cleaner layout so both plans breathe on every screen size. */
import React, { useEffect, useState } from 'react';
import { Check, Tag, CheckCircle2 } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { toast } from 'sonner';

export function BillingTab({ summary, memorialId }) {
  const [plans, setPlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoApplied, setPromoApplied] = useState(null); // {code, discount_percent}
  const [memSummary, setMemSummary] = useState(null);
  const [stripeMode, setStripeMode] = useState(null); // 'live' | 'test' | null

  useEffect(() => {
    api.get('/packages')
      .then(({ data }) => setPlans(Array.isArray(data?.packages) ? data.packages : []))
      .catch(() => setPlans([]));
    api.get('/billing/history')
      .then(({ data }) => setHistory(Array.isArray(data?.history) ? data.history : []))
      .catch(() => {});
    api.get('/billing/config')
      .then(({ data }) => setStripeMode(data?.mode || null))
      .catch(() => setStripeMode(null));
    if (memorialId) {
      api.get(`/memorials/${memorialId}/billing-summary`).then(({ data }) => setMemSummary(data)).catch(() => {});
    }
  }, [memorialId]);

  const isMemorialPaid = memSummary ? memSummary.plan_status === 'paid' : null;
  const memorialPlanId = memSummary?.plan_id || null;
  // iter77 — "Current plan" disables the Pay button. Only treat a memorial
  // as "currently on" a plan when it has actually been PAID. An unpaid
  // memorial may carry a pre-selected plan_id (e.g., "memorial") at the
  // creation step, but the customer still needs to be able to click and
  // pay for it — so we must NOT mark that plan as current.
  const current = isMemorialPaid
    ? (memorialPlanId || summary.plan.id)
    : null;

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoChecking(true);
    try {
      const { data } = await api.post('/promo/validate', { code: promoCode.trim() });
      if (data.valid) {
        setPromoApplied({ code: data.code, discount_percent: data.discount_percent });
        toast.success(`${data.discount_percent}% off applied`);
      } else {
        setPromoApplied(null);
        toast.error('That code is not valid or has expired.');
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPromoChecking(false);
    }
  };

  const clearPromo = () => {
    setPromoApplied(null);
    setPromoCode('');
  };

  const checkout = async (plan_id) => {
    setLoadingPlan(plan_id);
    try {
      const payload = {
        plan_id,
        billing_cycle: 'annual',
        origin_url: window.location.origin,
      };
      if (memorialId) payload.memorial_id = memorialId;
      if (promoApplied?.code) payload.promo_code = promoApplied.code;
      const { data } = await api.post('/billing/checkout', payload);
      window.location.href = data.url;
    } catch (e) {
      alert(formatApiError(e));
      setLoadingPlan(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="billing-tab">
      {memSummary && (
        <div
          className={`ml-card !p-5 ${isMemorialPaid ? 'border-emerald-300/60 bg-emerald-50/30' : 'border-amber-300/60 bg-amber-50/30'}`}
          data-testid="memorial-payment-status-card"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="ml-overline" data-testid="memorial-payment-status-label">
                {isMemorialPaid ? 'This memorial is active' : 'Activation required'}
              </div>
              <div className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">
                {memSummary.full_name}
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                {isMemorialPaid
                  ? <>Activated on <strong>{memSummary.paid_at?.slice(0, 10) || '—'}</strong> · Plan: <strong className="capitalize">{memSummary.plan_id || '—'}</strong> · QR + plaque ordering unlocked.</>
                  : <>Select a plan below to activate the QR code, unlock plaque orders, and publish this memorial. Each memorial is billed separately.</>
                }
              </p>
            </div>
            <span className={`text-xs uppercase tracking-[0.22em] px-3 py-1.5 rounded-full border font-semibold ${isMemorialPaid ? 'border-emerald-500/30 text-emerald-700 bg-emerald-50' : 'border-amber-500/40 text-amber-700 bg-amber-50'}`} data-testid="memorial-payment-status-pill">
              {isMemorialPaid ? 'Paid' : 'Unpaid'}
            </span>
          </div>
        </div>
      )}

      {/* Current plan — compact header card. Only shown once the memorial
          is actually paid; otherwise it's misleading because creation
          pre-tags a default plan_id before payment. */}
      {isMemorialPaid && (
      <div className="ml-card">
        <div
          className="inline-block text-[10px] tracking-[0.28em] uppercase font-bold px-3 py-1 rounded-full border"
          style={{
            color: '#557A4C',
            backgroundColor: 'rgba(164, 195, 154, 0.18)',
            borderColor: 'rgba(164, 195, 154, 0.55)',
          }}
          data-testid="current-plan-badge"
        >
          Current plan
        </div>
        <h2 className="font-serif text-3xl mt-3 text-[hsl(var(--primary))]">
          {summary.plan.name}
        </h2>
        <div className="mt-3 flex items-baseline gap-2 flex-wrap">
          <span className="font-serif text-4xl text-[hsl(var(--primary))]">
            ${summary.plan.price}
          </span>
          <span className="text-sm text-[hsl(var(--muted-foreground))]">
            one-time · then ${summary.plan.keeping_fee ?? 12}/year keeping fee
          </span>
        </div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-3">{summary.plan.tagline}</p>
        <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[hsl(var(--primary)/0.82)]">
          {summary.plan.features.map((f) => (
            <li key={f} className="flex gap-2 items-start">
              <Check className="w-4 h-4 text-[hsl(var(--accent))] mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
      )}

      {/* Promo code input */}
      <div className="ml-card !p-5" data-testid="billing-promo-card">
        {!promoApplied ? (
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[12rem]">
              <label className="ml-overline flex items-center gap-1 mb-2"><Tag size={12}/> Have a code?</label>
              <input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16))}
                placeholder="PROMO CODE"
                className="ml-input font-mono tracking-[0.25em]"
                data-testid="billing-promo-input"
              />
            </div>
            <button
              onClick={applyPromo}
              disabled={promoChecking || !promoCode.trim()}
              className="ml-btn-secondary"
              data-testid="billing-promo-apply-btn"
            >
              {promoChecking ? 'Checking…' : 'Apply'}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="billing-promo-applied">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 size={18}/>
              <span className="text-sm font-medium">
                <code className="font-mono">{promoApplied.code}</code> applied — <strong>{promoApplied.discount_percent}% off</strong> at checkout.
              </span>
            </div>
            <button onClick={clearPromo} className="ml-btn-secondary !text-xs" data-testid="billing-promo-clear-btn">Remove</button>
          </div>
        )}
      </div>

      {/* Upgrade / switch plan */}
      <div>
        <div className="mb-6">
          <div className="ml-overline">Plans</div>
          <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-1">
            Choose your memorial
          </h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-xl">
            Both plans are a one-time charge plus a small $12/year keeping fee —
            that's what funds hosting, backups, and ongoing care of your family's memorial.
          </p>
        </div>

        <div
          className="space-y-6 max-w-3xl"
          data-testid="billing-plans"
        >
          {plans.map((p) => {
            const isCurrent = p.id === current;
            const highlighted = p.highlighted;
            return (
              <div
                key={p.id}
                className={[
                  'ml-card relative flex flex-col p-8 sm:p-10',
                  highlighted
                    ? 'border-[hsl(var(--accent))] ring-1 ring-[hsl(var(--accent))]/35 bg-[hsl(var(--surface))]'
                    : '',
                ].join(' ')}
                data-testid={`billing-plan-${p.id}`}
              >
                {highlighted && (
                  <span
                    className="absolute top-4 right-4 text-[9px] tracking-[0.22em] uppercase font-semibold px-2.5 py-1 rounded-full bg-[hsl(var(--accent))] text-white shadow-sm"
                    data-testid={`billing-plan-${p.id}-badge`}
                  >
                    Most loved
                  </span>
                )}
                <div className="pr-20">
                  <div className="font-serif text-2xl sm:text-3xl text-[hsl(var(--primary))] leading-tight">
                    {p.name}
                  </div>
                  {p.tagline && (
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed">
                      {p.tagline}
                    </p>
                  )}
                </div>

                {/* Price block */}
                <div className="mt-8 pb-6 border-b border-[hsl(var(--primary)/0.1)]">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-serif text-5xl sm:text-6xl text-[hsl(var(--primary))] leading-none">
                      ${p.price}
                    </span>
                    <span className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--primary)/0.55)] font-semibold whitespace-nowrap">
                      one-time
                    </span>
                  </div>
                  <div className="mt-4 text-sm text-[hsl(var(--primary)/0.75)] leading-relaxed">
                    + <span className="font-semibold text-[hsl(var(--primary))]">${p.keeping_fee ?? 12}/year</span> keeping fee
                    <span className="block text-[hsl(var(--primary)/0.55)] text-xs mt-0.5">covers hosting, backups &amp; care</span>
                  </div>
                </div>

                {/* Features */}
                <ul className="mt-6 space-y-3 text-sm text-[hsl(var(--primary)/0.85)] flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-3 items-start leading-relaxed">
                      <Check className="w-4 h-4 text-[hsl(var(--accent))] mt-1 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  disabled={isCurrent || loadingPlan === p.id}
                  onClick={() => checkout(p.id)}
                  data-testid={`checkout-${p.id}-btn`}
                  style={isCurrent ? {
                    color: '#557A4C',
                    backgroundColor: 'rgba(164, 195, 154, 0.15)',
                    borderColor: 'rgba(164, 195, 154, 0.6)',
                  } : undefined}
                  className={[
                    'mt-8 w-full',
                    isCurrent
                      ? 'ml-btn-outline opacity-100 cursor-default font-semibold'
                      : highlighted
                        ? 'ml-btn-gold'
                        : 'ml-btn-primary',
                  ].join(' ')}
                >
                  {isCurrent
                    ? 'Current plan'
                    : loadingPlan === p.id
                      ? 'Opening checkout…'
                      : `Choose ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {history.length > 0 && (
        <div className="ml-card">
          <div className="ml-overline">Billing history</div>
          <h3 className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">Recent transactions</h3>
          <div className="mt-4 space-y-2 text-sm" data-testid="billing-history">
            {history.map((h) => (
              <div
                key={h.transaction_id}
                className="flex justify-between border-b border-border/70 pb-2 last:border-0"
              >
                <div>
                  <div className="font-medium capitalize">
                    {h.plan_id} · ${h.amount}
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">
                    {new Date(h.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="text-xs uppercase tracking-widest font-semibold text-[hsl(var(--accent))]">
                  {h.payment_status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {stripeMode === 'test' && (
        <div className="ml-card" data-testid="stripe-test-mode-banner">
          <div className="ml-overline">Test mode</div>
          <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
            Stripe is running in <strong>test mode</strong>. Use card{' '}
            <code className="bg-[hsl(var(--muted))] px-1 py-0.5 rounded">4242 4242 4242 4242</code>{' '}
            with any future expiry and any CVC.
          </div>
        </div>
      )}
    </div>
  );
}
